/* ============================================================
   Waste.jsx — svinn: kiosken registrerar vad som slängdes, och
   admin följer utvecklingen över tid.
   ============================================================ */

import { useState } from 'react';
import { Icon } from '../components/Icon.jsx';
import { Modal, Stepper, Stat, BarChart, askConfirm } from '../components/ui.jsx';
import { useStore, actions } from '../store/store.js';
import { kr, isUntracked, rangeFor, revenueBetween, wasteBetween, weeklySeries, monthlySeries, yearlySeries } from '../store/selectors.js';
import { PageHead, PeriodToggle } from '../components/admin-shell.jsx';

function SectionLabel({ children }) {
  return (
    <div style={{
      fontSize: 12, fontWeight: 800, letterSpacing: '.6px', textTransform: 'uppercase',
      color: 'var(--muted)', margin: '0 2px 10px',
    }}>{children}</div>
  );
}

export function WasteDialog({ onClose, onDone, atLogout }) {
  const state = useStore();
  const [q, setQ] = useState({});     // 'ing:id' / 'prod:id' → antal
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const [showRest, setShowRest] = useState(false);

  /* What actually gets thrown away: lagervaror plus varor without a recipe.
     Recipe products are deliberately absent — wasting a "Korv med bröd" would
     deduct the bread too, and the bread keeps until the next match. Varor i
     grupper utan lagersaldo (Hembakat) har inget lager att dra svinn från. */
  const rows = [
    ...state.ingredients.map(i => ({ kind: 'ing', id: i.id, name: i.name, emoji: i.emoji, cost: i.cost, stock: i.stock, perishable: !!i.perishable })),
    ...state.products.filter(p => !p.recipe && !isUntracked(state, p)).map(p => ({ kind: 'prod', id: p.id, name: p.name, emoji: p.emoji, cost: p.cost, stock: p.stock, perishable: false })),
  ];
  const key = r => r.kind + ':' + r.id;
  const val = r => q[key(r)] || 0;
  const picked = rows.filter(r => val(r) > 0);

  const perishable = rows.filter(r => r.perishable);
  const rest = rows.filter(r => !r.perishable);

  /* A row already ticked must stay visible even if it sits under "Övrigt". */
  const anyRestPicked = rest.some(r => val(r) > 0);
  const totQty = picked.reduce((a, r) => a + val(r), 0);
  const totVal = picked.reduce((a, r) => a + val(r) * r.cost, 0);

  const row = r => (
    <div key={key(r)} style={{
      display: 'flex', alignItems: 'center', gap: 11, padding: '9px 12px', borderRadius: 13,
      background: val(r) > 0 ? '#fbeed8' : '#fbf8f0', border: '1.5px solid ' + (val(r) > 0 ? '#f0d9ae' : 'transparent'),
    }}>
      <span style={{ fontSize: 20 }}>{r.emoji}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 700, fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.name}</div>
        <div style={{ fontSize: 11.5, color: 'var(--muted)', fontWeight: 600 }}>{r.stock} i lager · {r.cost} kr/st</div>
      </div>
      <Stepper value={val(r)} min={0} onChange={v => setQ(s => ({ ...s, [key(r)]: Math.max(0, Math.min(v, r.stock)) }))} />
    </div>
  );

  async function save() {
    if (busy) return;
    setBusy(true);
    try {
      if (picked.length) {
        await actions.addWaste(picked.map(r => ({ kind: r.kind, id: r.id, name: r.name, qty: val(r) })), note);
      }
      (onDone || onClose)();
    } catch {
      /* reported by the store — keep the dialog open so nothing is lost */
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title={atLogout ? 'Innan ni går — något som slängdes?' : 'Registrera svinn'} onClose={onClose} wide
      footer={<>
        <div style={{ flex: 1, fontSize: 13.5, fontWeight: 700, color: totQty ? 'var(--orange-deep)' : 'var(--muted)' }}>
          {totQty ? `${totQty} st · ${kr(totVal)} i inköpsvärde` : 'Inget svinn valt'}
        </div>
        <button className="btn btn-ghost" onClick={atLogout ? (onDone || onClose) : onClose}>
          {atLogout ? 'Inget svinn – logga ut' : 'Avbryt'}
        </button>
        <button className="btn btn-primary" disabled={!picked.length || busy} onClick={save}>
          <Icon name="check" size={19} /> {atLogout ? 'Spara & logga ut' : 'Spara svinn'}
        </button>
      </>}>
      <p style={{ margin: '0 0 12px', fontSize: 14.5, color: 'var(--ink-soft)', lineHeight: 1.55, fontWeight: 600 }}>
        Vad slängdes efter passet? Fyll i antalet så dras det från lagret, och admin kan följa svinnet över tid.
      </p>
      {/* Bread keeps to the next match — only the grilled and boiled things
          actually get binned, and counting bread here would drain a lager
          that is still full. */}
      <div className="card-flat" style={{ display: 'flex', gap: 11, alignItems: 'flex-start', padding: '12px 15px', marginBottom: 18, borderLeft: '5px solid var(--orange)' }}>
        <Icon name="info" size={19} color="var(--orange-deep)" />
        <div style={{ fontSize: 13.5, lineHeight: 1.5, fontWeight: 600, color: 'var(--ink-soft)' }}>
          Slängde ni kokt korv eller grillade burgare? Räkna <b>bara själva korven och burgaren</b> – brödet
          ligger kvar och används nästa match. Bröd fyller ni bara i om ni faktiskt slängde bröd.
        </div>
      </div>
      {/* Korv and burgare first — that is what the bin actually sees. Everything
          else is collapsed so a 25-row sortiment does not bury them. */}
      {perishable.length > 0 && (
        <>
          <SectionLabel>Tillagas i förväg – slängs oftast</SectionLabel>
          <div className="grid-fill">{perishable.map(row)}</div>
        </>
      )}

      {rest.length > 0 && (
        <>
          <button onClick={() => setShowRest(v => !v)} style={{
            display: 'flex', alignItems: 'center', gap: 9, width: '100%', marginTop: perishable.length ? 20 : 0,
            padding: '11px 14px', borderRadius: 12, border: '1.5px solid var(--line)', background: '#fff',
            fontWeight: 700, fontSize: 14, color: 'var(--ink-soft)', cursor: 'pointer',
          }}>
            <Icon name={showRest || anyRestPicked ? 'minus' : 'plus'} size={17} color="var(--green)" />
            Något annat som slängdes? ({rest.length} varor)
          </button>
          {(showRest || anyRestPicked) && <div className="grid-fill" style={{ marginTop: 12 }}>{rest.map(row)}</div>}
        </>
      )}

      <div className="field" style={{ marginTop: 18 }}>
        <label>Kommentar (valfritt)</label>
        <input className="input" value={note} onChange={e => setNote(e.target.value)} placeholder="T.ex. grillade för många i pausen" />
      </div>
    </Modal>
  );
}

/* ---------------- ADMIN: svinnöversikt ---------------- */
export function WasteAdmin() {
  const state = useStore();
  const [period, setPeriod] = useState('week');
  const [edit, setEdit] = useState(null);   // svinnposten som justeras
  const [from, to] = rangeFor(period);
  const cur = wasteBetween(state, from, to);
  const pLabel = { week: 'denna vecka', month: 'denna månad', year: 'i år' }[period];

  let chart, prefix = 'v.', labelKey = 'week', title;
  if (period === 'month') {
    chart = monthlySeries(state, 12).map(m => ({ ...m, rev: wasteBetween(state, m.from, m.to).value }));
    prefix = ''; labelKey = 'm'; title = 'Svinn per månad';
  } else if (period === 'year') {
    chart = yearlySeries(state, 3).map(y => ({ ...y, rev: wasteBetween(state, y.from, y.to).value }));
    prefix = ''; labelKey = 'y'; title = 'Svinn per år';
  } else {
    chart = weeklySeries(state, 10).map(w => ({ ...w, rev: wasteBetween(state, w.from, w.to).value }));
    title = 'Svinn per vecka';
  }

  const perItem = {};
  cur.rows.forEach(w => w.lines.forEach(l => {
    const m = perItem[l.id] || (perItem[l.id] = { name: l.name, qty: 0 });
    m.qty += l.qty;
  }));
  const worst = Object.values(perItem).sort((a, b) => b.qty - a.qty).slice(0, 5);
  const revP = revenueBetween(state, from, to);
  const share = revP.rev > 0 ? Math.round(cur.value / revP.rev * 100) : 0;

  return (
    <div className="scroll" style={{ height: '100%' }}>
      <PageHead title="Svinn" sub="Vad slängdes i kiosken? Följ utvecklingen och minska svinnet."
        right={<PeriodToggle value={period} onChange={setPeriod} />} />
      <div className="admin-body">
        <div className="grid-kpi" style={{ marginBottom: 16 }}>
          <Stat label={`Svinn ${pLabel}`} value={cur.qty + ' st'} icon="trash" accent="var(--bad)" tone={cur.qty ? 'var(--bad)' : 'var(--ink)'} />
          <Stat label="Kostnad" value={kr(cur.value)} icon="money" accent="var(--orange)" tone={cur.value ? 'var(--orange-deep)' : 'var(--ink)'} sub="till inköpspris" />
          <Stat label="Andel av intäkt" value={share + '%'} icon="chart" accent="#a65a78" />
          <Stat label="Kiosktillfällen" value={cur.rows.length} icon="calendar" accent="#3f8197" sub="med registrerat svinn" />
        </div>

        <div className="grid-main" style={{ marginBottom: 16 }}>
          <div className="card" style={{ padding: '20px 22px' }}>
            <div style={{ fontFamily: 'var(--display)', fontWeight: 800, fontSize: 18, marginBottom: 6 }}>{title}</div>
            <BarChart data={chart} valueKey="rev" labelKey={labelKey} prefix={prefix} height={180} color="var(--orange)" />
          </div>
          <div className="card" style={{ padding: '20px 22px' }}>
            <div style={{ fontFamily: 'var(--display)', fontWeight: 800, fontSize: 18, marginBottom: 14 }}>Slängs mest {pLabel}</div>
            {worst.length === 0 && <div style={{ color: 'var(--muted)', fontSize: 14, fontWeight: 600 }}>Inget svinn registrerat. 👏</div>}
            {worst.map((w, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '9px 0', borderBottom: i < worst.length - 1 ? '1px solid var(--line-soft)' : 'none' }}>
                <div style={{ flex: 1, fontWeight: 700, fontSize: 14.5 }}>{w.name}</div>
                <div style={{ fontWeight: 800, fontFamily: 'var(--display)', fontSize: 16, color: 'var(--orange-deep)' }}>{w.qty} st</div>
              </div>
            ))}
          </div>
        </div>

        <div className="card" style={{ overflow: 'hidden' }}>
          <div style={{ padding: '16px 22px 10px', fontFamily: 'var(--display)', fontWeight: 800, fontSize: 18 }}>Per kiosktillfälle</div>
          {cur.rows.length === 0 && <div style={{ padding: '8px 22px 20px', color: 'var(--muted)', fontWeight: 600, fontSize: 14 }}>Inga registreringar i perioden.</div>}
          {cur.rows.map(w => (
            <div key={w.id} className="waste-row" style={{ display: 'flex', alignItems: 'flex-start', gap: 16, padding: '13px 22px', borderTop: '1px solid var(--line-soft)' }}>
              <div style={{ width: 88, flexShrink: 0 }}>
                <div style={{ fontWeight: 800, fontSize: 14.5 }}>{new Date(w.ts).toLocaleDateString('sv-SE', { day: 'numeric', month: 'short' })}</div>
                <div style={{ fontSize: 12, color: 'var(--muted)', fontWeight: 700 }}>v.{w.week} · {new Date(w.ts).toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' })}</div>
              </div>
              <div className="waste-team" style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--green-deep)' }}>{w.team || '—'}</div>
              <div className="waste-lines" style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {w.lines.map((l, j) => (
                    <span key={j} style={{ background: '#fbeed8', borderRadius: 8, padding: '4px 9px', fontSize: 13, fontWeight: 700, color: 'var(--orange-deep)' }}>{l.qty}× {l.name}</span>
                  ))}
                  {w.note && <span style={{ fontSize: 13, color: 'var(--muted)', fontWeight: 600, alignSelf: 'center' }}>„{w.note}”</span>}
                </div>
                {w.editedAt && <EditedStamp record={w} />}
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <div style={{ fontFamily: 'var(--display)', fontWeight: 800, fontSize: 17, color: 'var(--orange-deep)' }}>{kr(w.value)}</div>
                <div style={{ fontSize: 12, color: 'var(--muted)', fontWeight: 700 }}>{w.qty} st</div>
              </div>
              <button className="btn btn-icon btn-ghost" style={{ width: 36, height: 36 }} title="Justera antalen"
                onClick={() => setEdit(w)}><Icon name="edit" size={15} /></button>
              <button className="btn btn-icon btn-ghost" style={{ width: 36, height: 36 }} title="Ta bort registreringen" onClick={() => {
                askConfirm('Registreringen tas bort (lagret justeras inte tillbaka).',
                  { title: 'Ta bort svinnpost?', danger: true, confirmLabel: 'Ta bort' })
                  .then(ok => ok && actions.deleteWaste(w));
              }}><Icon name="trash" size={15} /></button>
            </div>
          ))}
        </div>
      </div>
      {edit && <WasteEditDialog record={edit} onClose={() => setEdit(null)} />}
    </div>
  );
}

/* Syns på varje post admin rört, så ingen tar siffrorna för kioskens egna. */
function EditedStamp({ record }) {
  const when = new Date(record.editedAt).toLocaleString('sv-SE', {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  });
  return (
    <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--orange-deep)', marginTop: 5 }}>
      ✏️ Justerad av {record.editedBy || 'Admin'} · {when}
    </div>
  );
}

/**
 * Admin rättar antalen i efterhand — laget kan ha slagit in fel i stressen.
 * Bara rader som redan finns går att ändra; 0 stryker raden, och vill man bli
 * av med hela registreringen finns papperskorgen bredvid.
 */
function WasteEditDialog({ record, onClose }) {
  const state = useStore();
  const key = l => l.kind + ':' + l.id;
  const [qty, setQty] = useState(() => Object.fromEntries(record.lines.map(l => [key(l), l.qty])));
  const [note, setNote] = useState(record.note || '');
  const [busy, setBusy] = useState(false);

  const src = l => (l.kind === 'ing' ? state.ingredients : state.products).find(x => x.id === l.id);
  /* Priset på raden är det som gällde när varan slängdes; gamla poster saknar
     det och får låna dagens ur katalogen — precis som servern räknar. */
  const costOf = l => (l.cost != null ? l.cost : (src(l) || {}).cost || 0);

  const kept = record.lines.filter(l => (qty[key(l)] || 0) > 0);
  const totQty = kept.reduce((a, l) => a + qty[key(l)], 0);
  const totVal = kept.reduce((a, l) => a + qty[key(l)] * costOf(l), 0);
  const changed = record.lines.some(l => qty[key(l)] !== l.qty) || note !== (record.note || '');

  async function save() {
    if (!kept.length || !changed || busy) return;
    setBusy(true);
    try {
      const lines = record.lines.map(l => ({ kind: l.kind, id: l.id, qty: qty[key(l)] || 0 }));
      await actions.updateWaste(record, lines, note);
      onClose();
    } catch {
      /* the store already surfaced the message — keep the dialog open */
      setBusy(false);
    }
  }

  const when = new Date(record.ts).toLocaleDateString('sv-SE', { weekday: 'long', day: 'numeric', month: 'long' });

  return (
    <Modal title="Justera svinn" onClose={onClose}
      footer={<>
        <div style={{ flex: 1, fontSize: 13.5, fontWeight: 700, color: totQty ? 'var(--orange-deep)' : 'var(--bad)' }}>
          {totQty ? totQty + ' st · ' + kr(totVal) + ' i inköpsvärde' : 'Minst en rad måste ha ett antal'}
        </div>
        <button className="btn btn-ghost" onClick={onClose}>Avbryt</button>
        <button className="btn btn-primary" disabled={!kept.length || !changed || busy} onClick={save}>
          {busy ? <span className="spinner" /> : <Icon name="check" size={19} />} Spara ändringen
        </button>
      </>}>
      <p style={{ margin: '0 0 6px', fontSize: 14.5, fontWeight: 700, color: 'var(--ink)' }}>
        {when.charAt(0).toUpperCase() + when.slice(1)}{record.team ? ' · ' + record.team : ''}
      </p>
      <p style={{ margin: '0 0 16px', fontSize: 14, color: 'var(--ink-soft)', lineHeight: 1.55, fontWeight: 600 }}>
        Rätta antalen om något blev fel i stressen. Posten märks med att du justerat den
        och när. <b>Lagret rörs inte</b> — stämmer inte saldot rättar du det under Inventering.
      </p>

      {record.lines.map(l => {
        const n = qty[key(l)] || 0;
        return (
          <div key={key(l)} style={{
            display: 'flex', alignItems: 'center', gap: 11, padding: '9px 12px', borderRadius: 13, marginBottom: 8,
            background: n > 0 ? '#fbf8f0' : '#f1ebdf', opacity: n > 0 ? 1 : .65,
          }}>
            <span style={{ fontSize: 20 }}>{(src(l) || {}).emoji || '📦'}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{l.name}</div>
              <div style={{ fontSize: 11.5, color: 'var(--muted)', fontWeight: 600 }}>
                {costOf(l)} kr/st · registrerat {l.qty} st{n === 0 ? ' · raden stryks' : ''}
              </div>
            </div>
            <Stepper value={n} min={0} onChange={v => setQty(q => ({ ...q, [key(l)]: Math.max(0, v) }))} />
          </div>
        );
      })}

      <div className="field" style={{ marginTop: 14 }}>
        <label>Kommentar</label>
        <input className="input" value={note} onChange={e => setNote(e.target.value)}
          placeholder="T.ex. räknade fel på korven" />
      </div>

      {record.editedAt && <div style={{ marginTop: 12 }}><EditedStamp record={record} /></div>}
    </Modal>
  );
}
